import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

/**
 * Service to dynamically load the translation tool web component
 * This loads the script outside of Angular's build process, avoiding compilation issues
 */
@Injectable({
  providedIn: 'root',
})
export class TranslationToolLoaderService {
  private loadPromise: Promise<void> | null = null;
  private loaded = false;

  /**
   * Dynamically loads the translation tool web component script
   * Returns an Observable that completes when the component is ready to use
   */
  loadTranslationTool(): Observable<void> {
    // If already loaded, return immediately
    if (this.loaded) {
      return of(undefined);
    }

    // If load is in progress, reuse the same promise
    if (this.loadPromise) {
      return from(this.loadPromise);
    }

    // Start loading the script
    this.loadPromise = this.loadScript();

    return from(this.loadPromise).pipe(
      map(() => {
        this.loaded = true;
      }),
      catchError((error) => {
        console.error('[Translation Tool Loader] Failed to load:', error);
        this.loadPromise = null; // Reset so it can be retried
        return throwError(() => error);
      }),
      shareReplay(1),
    );
  }

  /**
   * Creates and appends a script tag to load the translation tool
   */
  private async loadScript(): Promise<void> {
    return await new Promise((resolve, reject) => {
      // Check if already loaded (in case multiple calls happen simultaneously)
      if (customElements.get('learningtools-translation-tool')) {
        // console.log('[Translation Tool Loader] Component already registered');
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = this.getScriptPath();
      script.async = true;

      script.onload = () => {
        // console.log('[Translation Tool Loader] Script loaded successfully');

        // Wait a bit for the custom element to register
        this.waitForCustomElement('learningtools-translation-tool', 5000)
          .then(() => {
            // console.log('[Translation Tool Loader] Custom element registered');
            resolve();
          })
          .catch((error) => {
            console.error('[Translation Tool Loader] Custom element registration timeout');
            reject(error);
          });
      };

      script.onerror = (error) => {
        console.error('[Translation Tool Loader] Script load error:', error);
        document.head.removeChild(script);
        reject(new Error('Failed to load translation tool script'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Gets the path to the translation tool script
   * Loads from assets folder where files are copied during build
   */
  private getScriptPath(): string {
    // Files are copied to assets/translation-tool/ during build via angular.json
    return '/assets/translation-tool/main.js';
  }

  /**
   * Waits for a custom element to be defined, with timeout
   */
  private async waitForCustomElement(tagName: string, timeout: number): Promise<void> {
    return await new Promise((resolve, reject) => {
      if (customElements.get(tagName)) {
        resolve();
        return;
      }

      const timeoutId = setTimeout(() => {
        reject(new Error(`Timeout waiting for custom element: ${tagName}`));
      }, timeout);

      customElements.whenDefined(tagName)
        .then(() => {
          clearTimeout(timeoutId);
          resolve();
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          reject(error);
        });
    });
  }

  /**
   * Check if the translation tool is currently loaded and available
   */
  isLoaded(): boolean {
    return this.loaded && !!customElements.get('learningtools-translation-tool');
  }
}
